import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { Shield, ArrowLeft, Youtube } from 'lucide-react';

export default function PlayerProfile() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayerProfile();
  }, [playerId]);

  const fetchPlayerProfile = async () => {
    try {
      const response = await api.get(`/players/${playerId}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch player profile');
    } finally {
      setLoading(false);
    }
  };

  const extractYoutubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-primary-neon text-xl">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold mb-4">Player Not Found</h1>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const { profile, user, videos } = data;

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="player-profile-page">
      <nav className="border-b border-zinc-800 bg-zinc-900/50" data-testid="profile-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png"
                alt="FutureXV"
                className="h-10"
              />
            </div>
            <Button 
              onClick={() => navigate(-1)} 
              variant="ghost" 
              className="text-zinc-400 hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-zinc-900 border-zinc-800 p-8 mb-8" data-testid="profile-header">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-4xl font-heading font-bold mb-2" data-testid="player-name">
                {user.full_name}
              </h1>
              <p className="text-xl text-zinc-400 mb-4">{profile.position}</p>
              {profile.verified && (
                <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20" data-testid="verified-badge">
                  <Shield className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="player-stat-card rounded-lg p-4">
              <p className="text-zinc-500 text-sm mb-1">Gender</p>
              <p className="text-2xl font-heading font-bold capitalize">{profile.gender}</p>
            </div>
            <div className="player-stat-card rounded-lg p-4">
              <p className="text-zinc-500 text-sm mb-1">Age</p>
              <p className="text-2xl font-heading font-bold">{profile.age}</p>
            </div>
            <div className="player-stat-card rounded-lg p-4">
              <p className="text-zinc-500 text-sm mb-1">Height</p>
              <p className="text-2xl font-heading font-bold">{profile.height} cm</p>
            </div>
            <div className="player-stat-card rounded-lg p-4">
              <p className="text-zinc-500 text-sm mb-1">Weight</p>
              <p className="text-2xl font-heading font-bold">{profile.weight} kg</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-6">
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <p className="text-zinc-500 text-sm mb-1">School/Club</p>
              <p className="text-lg font-semibold">{profile.school_club}</p>
            </div>
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <p className="text-zinc-500 text-sm mb-1">Province</p>
              <p className="text-lg font-semibold">{profile.province}</p>
            </div>
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <p className="text-zinc-500 text-sm mb-1">Town</p>
              <p className="text-lg font-semibold">{profile.town}</p>
            </div>
          </div>

          {profile.bio && (
            <div className="mt-6 bg-zinc-950 rounded-lg p-6 border border-zinc-800">
              <h3 className="text-lg font-heading font-bold mb-3">About</h3>
              <p className="text-zinc-300 leading-relaxed">{profile.bio}</p>
            </div>
          )}
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="videos-section">
          <h2 className="text-2xl font-heading font-bold mb-6">Match Videos</h2>
          {videos.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Youtube className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No videos available</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {videos.map((video) => {
                const videoId = extractYoutubeId(video.video_url);
                return (
                  <div key={video.id} className="bg-zinc-950 rounded-lg overflow-hidden border border-zinc-800" data-testid={`video-${video.id}`}>
                    {videoId ? (
                      <div className="video-container">
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="aspect-video bg-zinc-900 flex items-center justify-center">
                        <Youtube className="w-12 h-12 text-zinc-700" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-heading font-bold text-lg mb-1">{video.title}</h3>
                      {video.description && <p className="text-zinc-400 text-sm">{video.description}</p>}
                      <Badge className="mt-2 bg-primary-neon/10 text-primary-neon border-0">
                        {video.video_type === 'highlight' ? 'Highlight Reel' : 'Full Match'}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
